/**
 * Parse raw text/event-stream chunks without assuming network chunks align with
 * SSE event boundaries.
 *
 * @param {(event: string) => void} onEvent
 * @returns {{push: (chunk: string) => void, finish: () => void}}
 */
export function createSseEventParser (onEvent) {
  let buffer = ''

  const emitBlock = (block) => {
    if (!block) return
    const dataLines = block
      .split(/\r?\n/)
      .filter(line => line.startsWith('data:'))
      // Spring's SSE writer emits "data:" directly followed by the String
      // payload. A leading space therefore belongs to the AI token.
      .map(line => line.slice(5))

    if (dataLines.length) {
      onEvent(dataLines.join('\n'))
    }
  }

  const drain = () => {
    let boundary = buffer.match(/\r?\n\r?\n/)
    while (boundary && boundary.index != null) {
      const block = buffer.slice(0, boundary.index)
      buffer = buffer.slice(boundary.index + boundary[0].length)
      emitBlock(block)
      boundary = buffer.match(/\r?\n\r?\n/)
    }
  }

  return {
    push (chunk) {
      if (typeof chunk !== 'string' || !chunk) return
      buffer += chunk
      drain()
    },
    finish () {
      drain()
      if (buffer.trim()) emitBlock(buffer)
      buffer = ''
    }
  }
}

/**
 * @param {string} value
 * @returns {{code: string, message: string}|null}
 */
export function parseCourseRegenerationError (value) {
  if (typeof value !== 'string') return null
  const match = value.trim().match(
    /^\[ERROR\]\s*(?:([A-Z][A-Z0-9_]+)\s*:)?\s*(.*)$/s
  )
  if (!match) return null
  return {
    code: match[1] || 'REGENERATION_GENERATION_FAILED',
    message: match[2] || 'Course regeneration failed'
  }
}

/**
 * Parse the course regeneration protocol:
 * streamed lesson text, [STAGE_DONE], then the final Course JSON.
 *
 * @param {{onContent?: (content: string) => void}} handlers
 * @returns {{
 *   push: (event: string) => void,
 *   finish: () => {course: Object|null, error: Object|null, stageDone: boolean}
 * }}
 */
export function createCourseRegenerationStreamParser (handlers = {}) {
  let stageDone = false
  let jsonBuffer = ''
  let course = null
  let error = null

  const tryParseCourse = () => {
    if (!jsonBuffer || course || error) return
    try {
      course = JSON.parse(jsonBuffer)
    } catch (parseError) {
      // JSON can be split across network/SSE chunks; wait for more data.
    }
  }

  return {
    push (event) {
      if (typeof event !== 'string') return
      const trimmed = event.trim()
      if (trimmed === '[DONE]') return

      if (!trimmed) {
        if (!stageDone && handlers.onContent) handlers.onContent(event || '\n')
        return
      }

      const parsedError = parseCourseRegenerationError(trimmed)
      if (parsedError) {
        error = parsedError
        return
      }

      if (trimmed === '[STAGE_DONE]') {
        stageDone = true
        return
      }

      if (stageDone) {
        jsonBuffer += trimmed
        tryParseCourse()
        return
      }

      if (handlers.onContent) handlers.onContent(event)
    },
    finish () {
      tryParseCourse()
      if (!course && !error) {
        error = {
          code: 'REGENERATION_GENERATION_FAILED',
          message: stageDone
            ? 'Course response was incomplete'
            : 'Course regeneration returned no result'
        }
      }
      return { course, error, stageDone }
    }
  }
}

/**
 * Preserve readable spacing when AI tokens arrive without their leading space.
 *
 * @param {string} current
 * @param {string} chunk
 * @returns {string}
 */
export function appendCourseStreamText (current, chunk) {
  if (chunk == null) return current
  return current + chunk
}
